import React, {
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect
} from 'react';
import { Box, IconButton } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  TableV3FilterItem,
  TableV3ServerFilterOptions
} from 'common/table/types/TableV3';
import { DropdownList } from 'system';
import { useTableFilterSelection } from 'common/table/hooks/useTableFilterSelection';
import { useTableFilterApply } from 'common/table/hooks/useTableFilterApply';
import { TableV3FilterSelectionContext } from 'common/table/components/TableV3FilterSelectionContext';
import { pinSelectedFirst } from 'common/table/utils/pinSelectedFirst';
import {
  FILTER_DROPDOWN_MAX_HEIGHT,
  FILTER_SEARCH_MIN_LENGTH
} from 'common/table/styles/filterDropdown';

type TableV3ServerFilterButtonOptions = TableV3ServerFilterOptions & {
  selectedValues: string[];
};

type TableV3ServerFilterButtonProps = {
  filterOptions: TableV3ServerFilterButtonOptions;
  isSorted?: boolean;
  onFilterSelect: (item: TableV3FilterItem) => void;
  onFilterApply?: (items: TableV3FilterItem[]) => void;
};

export type TableV3ServerFilterButtonRef = {
  openFilterDialog: () => void;
};

export const TableV3ServerFilterButton = forwardRef<
  TableV3ServerFilterButtonRef,
  TableV3ServerFilterButtonProps
>(
  (
    { filterOptions, isSorted = false, onFilterSelect, onFilterApply },
    ref
  ): React.ReactElement => {
    const committedValues = filterOptions.selectedValues;
    const requiresApply = filterOptions.requiresApply ?? false;

    const { selected, toggle, setSelection } = useTableFilterSelection(
      committedValues,
      filterOptions.selectionType
    );
    const [searchQuery, setSearchQuery] = useState('');
    const [items, setItems] = useState<TableV3FilterItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);

    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Each search replaces `items`, so labels have to outlive it.
    const knownLabelsRef = useRef(new Map<string, string>());
    filterOptions.selectedItems?.forEach((item) =>
      knownLabelsRef.current.set(item.value, item.label)
    );
    items.forEach((item) => knownLabelsRef.current.set(item.value, item.label));

    const minSearchLength =
      filterOptions.minSearchLength ?? FILTER_SEARCH_MIN_LENGTH;

    const getSelectedItems = () =>
      selected.map((value) => ({
        value,
        label: knownLabelsRef.current.get(value) ?? value
      }));

    const { open, setOpen, deferral, confirmActions, toggleOpen, dismiss } =
      useTableFilterApply({
        requiresApply,
        committedValues,
        getSelectedItems,
        setSelection,
        onFilterApply
      });

    useImperativeHandle(ref, () => ({
      openFilterDialog: () => setOpen(true)
    }));

    const runSearch = (term: string, delay: number) => {
      if (!filterOptions.onSearchFilter) return;

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      setIsLoading(true);

      const timeout = setTimeout(async () => {
        const result = await filterOptions.onSearchFilter!(term).catch(
          () => []
        );

        if (debounceTimeoutRef.current !== timeout) return;

        debounceTimeoutRef.current = null;
        setItems(result);
        setIsLoading(false);
      }, delay);

      debounceTimeoutRef.current = timeout;
    };

    // Load initial items when dropdown opens
    useEffect(() => {
      if (open && !hasLoadedInitial && filterOptions.onSearchFilter) {
        setHasLoadedInitial(true);
        runSearch('', 0);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, hasLoadedInitial, filterOptions]);

    // Each close forgets the search, so the next open starts from the full
    // list rather than a stale result or "no options" message.
    useEffect(() => {
      if (open) return;
      const wasPending = debounceTimeoutRef.current !== null;
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      setIsLoading(false);
      // A search replaced the options, or a load was dropped unfinished, so
      // the next open loads them again.
      if (wasPending || searchQuery.trim()) setHasLoadedInitial(false);
      setSearchQuery('');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Applied values the unfiltered page does not include still go on top.
    const missingSelectedItems = searchQuery
      ? []
      : (filterOptions.selectedItems ?? []).filter(
          (selectedItem) =>
            committedValues.includes(selectedItem.value) &&
            !items.some((item) => item.value === selectedItem.value)
        );
    const visibleItems = pinSelectedFirst(
      [...missingSelectedItems, ...items],
      committedValues
    );

    const onFilterClick = (value: string) => {
      const item = visibleItems.find((i) => i.value === value);
      if (!item) {
        return;
      }

      toggle(value);
      // The sub-label is display only; keep it out of the filter state.
      if (!requiresApply) onFilterSelect({ value, label: item.label });
    };

    const handleSearchChange = async (query: string) => {
      setSearchQuery(query);

      if (!filterOptions.onSearchFilter) return;

      const term = query.trim();

      // Below the minimum the dropdown shows a hint rather than results, so
      // there is nothing to fetch. An emptied box is not below it: that
      // reloads the unfiltered options.
      if (term.length > 0 && term.length < minSearchLength) {
        setIsLoading(false);
        return;
      }

      runSearch(term, 750);
    };

    return (
      <Box>
        <IconButton
          ref={anchorRef}
          onClick={toggleOpen}
          sx={{
            color: isSorted ? 'customGrey.light' : 'customGrey.dark'
          }}
        >
          <FilterListIcon fontSize="small" />
        </IconButton>
        <DropdownList
          popperProps={{
            open,
            sx: {
              zIndex: 2
            },
            anchorEl: anchorRef.current,
            placement: 'bottom-end',
            modifiers: [
              {
                name: 'flip',
                enabled: false
              },
              {
                name: 'preventOverflow',
                options: {
                  padding: 32
                }
              }
            ]
          }}
          headerContent={
            filterOptions.headerContent && (
              <TableV3FilterSelectionContext.Provider
                value={{ setSelection, getSelectedItems, deferral }}
              >
                {filterOptions.headerContent}
              </TableV3FilterSelectionContext.Provider>
            )
          }
          showCheckboxes={filterOptions.showCheckboxes}
          showSelectedCount={requiresApply}
          wrapLabels
          confirmActions={confirmActions}
          maxHeight={FILTER_DROPDOWN_MAX_HEIGHT}
          searchOptions={{
            searchQuery,
            placeholder: filterOptions.searchPlaceholder,
            onChange: handleSearchChange,
            minSearchLength
          }}
          options={visibleItems.map((item) => ({
            value: item.value as string,
            label: item.label,
            subLabel: item.subLabel
          }))}
          selectedValues={selected}
          isLoading={isLoading}
          onSelect={onFilterClick}
          onClickAway={(event) => dismiss(event, anchorRef.current)}
        />
      </Box>
    );
  }
);
